import Link from 'next/link';
import type { NextRouter } from 'next/router';
import { useRouter } from 'next/router';
import { type TFunction, useTranslation } from 'next-i18next';

import classNames from './BreadCrumb.module.scss';

export const BreadCrumb = ({ currentPage }: { currentPage?: string }) => {
  const router = useRouter();
  const { t } = useTranslation('common');

  return (
    <div className={classNames.breadcrumb}>
      <div>
        <Link href="/" className="hover-underline">
          HOME
        </Link>
      </div>
      {/* 関数宣言で作成した関数は hoisting が起こるので定義より前で呼び出しても問題ない */}
      {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
      {resolveBreadCrumbPaths({ router, t }).map((path, i, paths) => {
        return (
          <>
            <div style={{ margin: '0 8px', color: '#000000' }}>
              <span>／</span>
            </div>
            <div
              style={{
                color:
                  paths.length - 1 !== i || currentPage ? '#666666' : '#000000',
              }}
            >
              {path.href ? (
                <Link href={path.href} className="hover-underline">
                  {path.text}
                </Link>
              ) : (
                <span>{path.text}</span>
              )}
            </div>
            {currentPage && (
              <>
                <div style={{ margin: '0 8px', color: '#000000' }}>
                  <span>／</span>
                </div>
                <div style={{ whiteSpace: 'wrap', color: '#000000' }}>
                  <span>{currentPage}</span>
                </div>
              </>
            )}
          </>
        );
      })}
    </div>
  );
};

export function resolveBreadCrumbPaths({
  router,
  t,
}: {
  router: NextRouter;
  t: TFunction;
}): { text: string; href?: string }[] {
  switch (router.pathname) {
    case '/about':
      return [{ text: t('事業所紹介') }];
    case '/services':
      return [{ text: t('サービス紹介'), href: '/services/?id=32ajwe951' }];
    case '/strengths':
      return [{ text: t('私たちの強み') }];
    case '/hiring':
      return [{ text: t('採用情報') }];
    case '/blog':
      return [{ text: t('ナレッジブログ') }];
    case '/blog/[id]':
      return [{ text: t('ナレッジブログ'), href: '/blog' }];
    case '/faq':
      return [{ text: t('よくある質問') }];
    case '/hiring/form':
      return [
        { text: t('採用情報'), href: '/hiring' },
        { text: t('採用応募フォーム（入力画面）') },
      ];
    case '/hiring/thanks':
      return [
        { text: t('採用情報'), href: '/hiring' },
        { text: t('採用応募フォーム（入力画面）'), href: '/hiring/form' },
        { text: t('応募完了（完了画面）') },
      ];
    case '/contact':
    case '/contact/materials':
      return t
        ? [{ text: t('お問い合わせ') }]
        : [{ text: 'お問い合わせ' }];
    case '/contact/thanks':
      return t
        ? [
            { text: t('お問い合わせ'), href: '/contact' },
            { text: t('お問い合わせ完了') },
          ]
        : [
            { text: 'お問い合わせ', href: '/contact' },
            { text: 'お問い合わせ完了' },
          ];
    case '/contact/materials/thanks':
      return t
        ? [
            { text: t('contact_form'), href: '/contact' },
            { text: t('contact_materials_thanks') },
          ]
        : [
            { text: 'お問い合わせ', href: '/contact' },
            { text: '資料ダウンロード' },
          ];
    case '/news':
      return [{ text: t('お知らせ') }];
    case '/privacy-policy':
      return [{ text: t('プライバシーポリシー') }];
    default:
      // Dynamic Routing のパスは正規表現で検証する
      switch (true) {
        case /^\/news\/.+$/.test(router.pathname): {
          // NOTE: 下記のように id を取得しても "[id]" という文字列が取れるだけだった
          // const paths = router.pathname.split('/').filter(Boolean);
          // const id = paths[paths.length - 1]?.toString() || '';
          return [{ text: t('お知らせ'), href: '/news' }];
        }
        default:
          return [];
      }
  }
}
